import { BillLineItemKind } from "@common/types/bill";
import { InvoiceLineItemKind } from "@common/types/invoice";
import { fromMonetaryToInt, sumMonetaryValues } from "@common/helpers/monetary";

interface LineItemForSorting {
    sourceId?: string | null;
    id?: string | null;
}

interface LineItemForByLineItemView extends LineItemForSorting {
    kind: InvoiceLineItemKind;
    amount: number;
    matchedAmount?: number;
}

type AdjustmentKind =
    | InvoiceLineItemKind.TAX
    | InvoiceLineItemKind.DISCOUNT
    | InvoiceLineItemKind.DEPOSIT;

// Per-adjustment-kind rendering: "each" keeps every underlying line as its own
// row, "collapsed" merges the kind's header-level lines into a single row.
type AdjustmentVisibility = "each" | "collapsed";

// A row produced for the by-line-item view. `collapsedLineItemIds` is only set
// on a collapsed adjustment row and lists every underlying line's id, so bulk
// coding can update all of them instead of just the retained row's id.
type ByLineItemViewRow<T> = T & { collapsedLineItemIds?: string[] };

// The domain rule for "only `item` rows are real AR / bank-match exposure":
// synthetic kind: "tax" / "discount" / "deposit" split rows are derived from
// an item row's header tax/discount/deposit and are not independently
// matchable. Native-invoice save still writes purpose-account accrual coding
// (Sales Tax Payable / Discounts and Allowances / Customer Deposits) and
// copies the related item's receivables onto those rows.
export function isInvoiceItemLine<T extends { kind: InvoiceLineItemKind }>(
    line: T,
): boolean {
    return line.kind === InvoiceLineItemKind.ITEM;
}

export function isBillItemLine(line: {
    kind?: BillLineItemKind | null;
}): boolean {
    return line.kind == null || line.kind === BillLineItemKind.ITEM;
}

export function isBillDocumentAdjustmentLine(line: {
    kind?: string | null;
}): boolean {
    return (
        line.kind === BillLineItemKind.DISCOUNT ||
        line.kind === BillLineItemKind.DEPOSIT
    );
}

export function billLineContribution(line: {
    amount: number;
    kind?: BillLineItemKind | null;
}): number {
    return isBillDocumentAdjustmentLine(line) ? -line.amount : line.amount;
}

// Inverse of `isInvoiceItemLine`: selects the synthetic split rows. A type
// guard (not a plain boolean) so callers that need to book per-kind polarity
// get `kind` narrowed to the TAX | DISCOUNT | DEPOSIT union without a raw enum
// test.
export function isInvoiceTaxDiscountOrDepositLine<
    T extends { kind: InvoiceLineItemKind },
>(
    line: T,
): line is T & {
    kind:
        | InvoiceLineItemKind.TAX
        | InvoiceLineItemKind.DISCOUNT
        | InvoiceLineItemKind.DEPOSIT;
} {
    return (
        line.kind === InvoiceLineItemKind.TAX ||
        line.kind === InvoiceLineItemKind.DISCOUNT ||
        line.kind === InvoiceLineItemKind.DEPOSIT
    );
}

function adjustmentLinesForView<T extends LineItemForByLineItemView>({
    lines,
    kind,
    visibility,
}: {
    lines: T[];
    kind: AdjustmentKind;
    visibility: AdjustmentVisibility;
}): Array<ByLineItemViewRow<T>> {
    const kindLines = sortLineItems(lines.filter((line) => line.kind === kind));
    if (visibility === "each") {
        return kindLines;
    }

    const first = kindLines[0];
    const totalAmount = sumMonetaryValues(kindLines.map((line) => line.amount));
    if (!first || fromMonetaryToInt(totalAmount) === 0) {
        return [];
    }

    return [
        {
            ...first,
            amount: totalAmount,
            matchedAmount: sumMonetaryValues(
                kindLines.map((line) => line.matchedAmount ?? 0),
            ),
            collapsedLineItemIds: kindLines
                .map((line) => line.id)
                .filter((id): id is string => id != null),
        },
    ];
}

export function lineItemsForByLineItemView<
    T extends LineItemForByLineItemView,
>({
    lines,
    adjustmentVisibility,
}: {
    lines?: T[];
    adjustmentVisibility: Record<AdjustmentKind, AdjustmentVisibility>;
}): Array<ByLineItemViewRow<T>> {
    const invoiceLines = lines ?? [];
    const itemLines = invoiceLines.filter(
        (line) => !isInvoiceTaxDiscountOrDepositLine(line),
    );

    return sortLineItems([
        ...itemLines,
        ...adjustmentLinesForView({
            lines: invoiceLines,
            kind: InvoiceLineItemKind.TAX,
            visibility: adjustmentVisibility[InvoiceLineItemKind.TAX],
        }),
        ...adjustmentLinesForView({
            lines: invoiceLines,
            kind: InvoiceLineItemKind.DISCOUNT,
            visibility: adjustmentVisibility[InvoiceLineItemKind.DISCOUNT],
        }),
        ...adjustmentLinesForView({
            lines: invoiceLines,
            kind: InvoiceLineItemKind.DEPOSIT,
            visibility: adjustmentVisibility[InvoiceLineItemKind.DEPOSIT],
        }),
    ]);
}

const NEGATED_FOR_DISPLAY_KINDS: ReadonlySet<InvoiceLineItemKind> = new Set([
    InvoiceLineItemKind.DISCOUNT,
    InvoiceLineItemKind.DEPOSIT,
]);

// Discount and deposit lines are subtracted from the invoice total (see
// native-invoice.schema.ts: total = subtotal + tax - discount - deposit), so
// the per-line-item view must display them as a negative amount even though
// they are stored as a positive magnitude. Item and tax lines add to the
// total and display unchanged (sign preserved as entered). A discount/deposit
// line entered as a negative amount (edge case: a "negative discount" that
// increases the total instead of reducing it) displays positive, since
// negating a negative yields a positive.
export function applyInvoiceLineItemDisplaySign(
    kind: InvoiceLineItemKind,
    value: number,
): number {
    if (!NEGATED_FOR_DISPLAY_KINDS.has(kind)) {
        return value;
    }
    // `|| 0` avoids returning -0 for a zero value.
    return -value || 0;
}

export function getInvoiceLineItemDisplayAmount<
    T extends { kind: InvoiceLineItemKind; amount: number },
>(line: T): number {
    return applyInvoiceLineItemDisplaySign(line.kind, line.amount);
}

export function sortLineItems<T extends LineItemForSorting>(
    lineItems: T[],
): T[] {
    return [...lineItems].sort((left, right) => {
        const sourceOrder = (left.sourceId ?? "").localeCompare(
            right.sourceId ?? "",
        );
        if (sourceOrder !== 0) {
            return sourceOrder;
        }

        return (left.id ?? "").localeCompare(right.id ?? "");
    });
}
